import type { Lang } from "../lib/translations";
import type { LegalDocument } from "./types";

/**
 * Terms of Service content per language.
 *
 * Scoped to the reality of Minuta today: a macOS desktop app + a marketing
 * site + a subscription handled via Stripe. Jurisdictional language leans
 * conservative and user-friendly (no forced arbitration, right of
 * withdrawal acknowledged for EU consumers). The "governed by Portugal"
 * clause is a pragmatic default based on the operator's registered
 * jurisdiction at the time of writing — bring a lawyer in before
 * expanding to heavily regulated markets.
 *
 * Keep sections aligned between languages. When you update one, update
 * the other two at the same time so translations never drift.
 */
const LAST_UPDATED_ISO = "2026-05-14";

export function getTerms(lang: Lang): LegalDocument {
  switch (lang) {
    case "pt":
      return pt();
    case "es":
      return es();
    default:
      return en();
  }
}

function en(): LegalDocument {
  return {
    lastUpdated: `Last updated: ${LAST_UPDATED_ISO}`,
    preamble: [
      "These Terms of Service govern your use of the Minuta macOS application and the Minuta website at minutapp.tech. By creating an account, downloading the app, or paying for a subscription, you agree to these Terms.",
      "If you disagree with any part of these Terms, don't use the service. If you have questions before agreeing, write to support@minutapp.tech.",
    ],
    sections: [
      {
        heading: "The service",
        paragraphs: [
          "Minuta is a desktop application for supported macOS and Windows devices that captures meeting audio from apps on your computer, transcribes it locally, translates in real time, identifies speakers, summarises the meeting, and (on the Pro plan) suggests responses through an in-meeting AI copilot.",
          "All audio processing, transcription, translation, summarisation, and copilot reasoning runs on your computer. The service on our side is limited to the account system, subscription billing, the public website, and the downloadable installer.",
        ],
      },
      {
        heading: "Eligibility",
        paragraphs: [
          "You must be at least 16 years old to use Minuta. By using the service you confirm you have the capacity to enter into a binding contract under the law that applies to you.",
        ],
      },
      {
        heading: "Your account",
        paragraphs: [
          "You need an account to sign in to the desktop app and to manage a subscription. You are responsible for keeping your credentials safe and for every action taken from your account. Notify us at support@minutapp.tech if you believe your account has been compromised.",
          "One account is for one person. You can use it on as many of your own Macs as you like — don't share credentials with people who aren't you.",
        ],
      },
      {
        heading: "Plans and billing",
        paragraphs: [
          "Minuta offers a Free plan (1 hour of recording per month for local transcription and translation after any trial period) and a Pro plan (unlimited recording, automatic summaries, speaker identification, personal context, and the in-meeting AI copilot). Prices are shown at checkout in your local currency; Stripe applies the daily exchange rate when we display another currency.",
          "Pro is billed monthly and renews automatically until you cancel. Cancellation is available at any time through the Stripe Customer Portal linked from your account page. Access to Pro continues until the end of the current paid period.",
          "We may change prices or plan features with reasonable advance notice; changes will apply to the next billing cycle.",
        ],
      },
      {
        heading: "Free trial and refunds",
        paragraphs: [
          "If you're a consumer in the European Union, you have the statutory right of withdrawal within 14 days of purchase for digital services, provided you have not substantially used the service during that window. To exercise this right, write to support@minutapp.tech before the 14 days end.",
          "Outside the withdrawal period, subscription fees are non-refundable for the remainder of the paid period. We will, however, consider refunds in good faith for clear service failures — email us and we'll review.",
        ],
      },
      {
        heading: "Acceptable use",
        paragraphs: [
          "You are solely responsible for the legality of your use of Minuta. Laws in many jurisdictions require you to obtain the consent of everyone in a meeting before recording or transcribing it. It's your responsibility to understand and comply with the law that applies where you and the other participants are located.",
          "You agree not to: use Minuta for illegal activity; reverse-engineer, decompile, or tamper with the desktop application beyond what open-source licenses permit; attempt to bypass authentication, rate limiting, or billing; probe, scan, or otherwise exploit our infrastructure; or use the service in a way that harms other users or third parties.",
        ],
      },
      {
        heading: "Your content",
        paragraphs: [
          "Meetings, transcripts, translations, summaries, and any documents you attach as copilot context remain your property. We do not access them, we do not claim any licence over them, and we do not use them to train AI models — they live on your computer and our servers never see them.",
        ],
      },
      {
        heading: "Our intellectual property",
        paragraphs: [
          "The Minuta name, logo, website content, and the proprietary parts of the desktop application belong to the Minuta team. While your subscription is active, we grant you a limited, non-transferable, non-exclusive licence to use the software for personal or business meetings, subject to these Terms.",
          "The desktop app ships with open-source components (including whisper.cpp, llama.cpp, and model weights under their respective licences). Those components remain governed by their own licences, which are reproduced in the app bundle.",
        ],
      },
      {
        heading: "AI outputs",
        paragraphs: [
          "Transcriptions, translations, summaries, and copilot suggestions are produced by statistical models and may contain errors, omissions, or hallucinations. Do not treat them as authoritative records or as legal, medical, financial, or similar professional advice. You are responsible for reviewing outputs before acting on them.",
        ],
      },
      {
        heading: "Disclaimers",
        paragraphs: [
          "The service is provided \"as is\" and \"as available\". To the maximum extent permitted by law, we disclaim all warranties, express or implied, including warranties of merchantability, fitness for a particular purpose, and non-infringement. We don't promise the service will be uninterrupted, error-free, or meet every need.",
        ],
      },
      {
        heading: "Limitation of liability",
        paragraphs: [
          "To the maximum extent permitted by law, Minuta and its team will not be liable for any indirect, incidental, special, consequential, or punitive damages, or for any loss of profits, revenue, data, or goodwill, arising out of or related to the service.",
          "Our total aggregate liability to you for any claim arising out of or related to the service will not exceed the amount you paid us for Minuta in the 12 months immediately preceding the event giving rise to the claim.",
          "Nothing in these Terms limits liability that cannot be limited by law (for example, gross negligence, intentional misconduct, or consumer rights that cannot be waived).",
        ],
      },
      {
        heading: "Termination",
        paragraphs: [
          "You may stop using Minuta and delete your account at any time. If you have a Pro subscription, cancel it through the Customer Portal to stop future billing.",
          "We may suspend or terminate your access if you breach these Terms, if required by law, or if we decide to discontinue the service. We will give reasonable notice unless an immediate action is required to protect users or infrastructure.",
        ],
      },
      {
        heading: "Changes to these Terms",
        paragraphs: [
          "We may update these Terms from time to time. When we make a material change, we will update the \"last updated\" date above and, if you have an account, notify you by email. Continued use of the service after a change takes effect means you accept the updated Terms.",
        ],
      },
      {
        heading: "Governing law",
        paragraphs: [
          "These Terms are governed by the laws of Portugal, without regard to its conflict-of-law rules, and the courts of Portugal have exclusive jurisdiction over any dispute — except where mandatory consumer-protection laws in your country of residence provide otherwise.",
        ],
      },
    ],
    contact: "Questions about these Terms: support@minutapp.tech.",
  };
}

function pt(): LegalDocument {
  return {
    lastUpdated: `Última atualização: ${LAST_UPDATED_ISO}`,
    preamble: [
      "Estes Termos de Serviço regem o seu uso do aplicativo Minuta para macOS e do site Minuta em minutapp.tech. Ao criar uma conta, baixar o app ou pagar por uma assinatura, você concorda com estes Termos.",
      "Se discorda de qualquer parte destes Termos, não use o serviço. Se tiver dúvidas antes de concordar, escreva para support@minutapp.tech.",
    ],
    sections: [
      {
        heading: "O serviço",
        paragraphs: [
          "O Minuta é um app desktop para dispositivos macOS e Windows compatíveis que captura áudio de reuniões a partir dos apps no seu computador, transcreve localmente, traduz em tempo real, identifica falantes, gera ata da reunião e (no plano Pro) sugere respostas através de um copiloto de IA durante a própria reunião.",
          "Todo o processamento de áudio, transcrição, tradução, resumo e raciocínio do copiloto roda no seu computador. O serviço do nosso lado se limita ao sistema de contas, cobrança da assinatura, site público e instalador disponível para download.",
        ],
      },
      {
        heading: "Elegibilidade",
        paragraphs: [
          "Você precisa ter ao menos 16 anos para usar o Minuta. Ao usar o serviço, você confirma ter capacidade de celebrar um contrato vinculante conforme a lei que se aplica a você.",
        ],
      },
      {
        heading: "Sua conta",
        paragraphs: [
          "Você precisa de uma conta para entrar no app desktop e gerenciar uma assinatura. É sua responsabilidade manter suas credenciais seguras e toda ação feita a partir da sua conta. Avise em support@minutapp.tech caso acredite que sua conta foi comprometida.",
          "Uma conta é para uma pessoa. Você pode usá-la em quantos Macs seus quiser — não compartilhe credenciais com terceiros.",
        ],
      },
      {
        heading: "Planos e cobrança",
        paragraphs: [
          "O Minuta oferece um plano Free (1 hora de gravação por mês para transcrição e tradução locais depois de qualquer período de trial) e um plano Pro (gravação ilimitada, atas automáticas, identificação de falantes, contexto pessoal e copiloto de IA na reunião). Os preços aparecem no checkout na sua moeda local; a Stripe aplica o câmbio do dia quando mostramos outra moeda.",
          "O Pro é cobrado mensalmente e renova automaticamente até você cancelar. O cancelamento está disponível a qualquer momento pelo Portal do Cliente Stripe, linkado na página da sua conta. O acesso ao Pro segue até o fim do período pago vigente.",
          "Podemos alterar preços ou funcionalidades de plano com aviso prévio razoável; as alterações valem para o ciclo de cobrança seguinte.",
        ],
      },
      {
        heading: "Direito de arrependimento e reembolsos",
        paragraphs: [
          "Se você é consumidor na União Europeia, tem direito legal de arrependimento em até 14 dias após a compra para serviços digitais, desde que não tenha feito uso substancial do serviço nesse intervalo. Para exercer esse direito, escreva para support@minutapp.tech antes dos 14 dias. Se você é consumidor no Brasil, aplica-se o prazo de 7 dias previsto pelo art. 49 do CDC em contratações fora do estabelecimento comercial.",
          "Fora do prazo de arrependimento, as mensalidades não são reembolsáveis pelo restante do período pago. Ainda assim, analisamos reembolsos de boa-fé em casos claros de falha do serviço — basta escrever que olhamos.",
        ],
      },
      {
        heading: "Uso aceitável",
        paragraphs: [
          "Você é o único responsável pela legalidade do seu uso do Minuta. As leis de muitos países exigem o consentimento de todos os participantes de uma reunião antes de gravá-la ou transcrevê-la. É sua responsabilidade conhecer e cumprir a lei onde você e os demais participantes estão.",
          "Você concorda em não: usar o Minuta para atividades ilegais; fazer engenharia reversa, descompilar ou alterar o app desktop além do permitido pelas licenças open-source; tentar burlar autenticação, rate limiting ou cobrança; sondar, escanear ou explorar nossa infraestrutura; ou usar o serviço de forma que prejudique outros usuários ou terceiros.",
        ],
      },
      {
        heading: "Seu conteúdo",
        paragraphs: [
          "Reuniões, transcrições, traduções, atas e quaisquer documentos que você anexe como contexto do copiloto permanecem de sua propriedade. Não os acessamos, não reivindicamos licença sobre eles, e não os usamos para treinar modelos de IA — eles ficam no seu computador e nossos servidores nunca os veem.",
        ],
      },
      {
        heading: "Nossa propriedade intelectual",
        paragraphs: [
          "O nome Minuta, o logotipo, o conteúdo do site e as partes proprietárias do app desktop pertencem à equipe Minuta. Enquanto sua assinatura estiver ativa, concedemos a você uma licença limitada, não-transferível e não-exclusiva para usar o software em reuniões pessoais ou profissionais, conforme estes Termos.",
          "O app desktop inclui componentes open-source (entre outros: whisper.cpp, llama.cpp e pesos de modelo sob suas respectivas licenças). Esses componentes seguem as próprias licenças, reproduzidas no bundle do app.",
        ],
      },
      {
        heading: "Outputs de IA",
        paragraphs: [
          "Transcrições, traduções, resumos e sugestões do copiloto são produzidos por modelos estatísticos e podem conter erros, omissões ou alucinações. Não os trate como registros definitivos nem como consultoria jurídica, médica, financeira ou profissional equivalente. É sua responsabilidade revisar os outputs antes de agir com base neles.",
        ],
      },
      {
        heading: "Isenções",
        paragraphs: [
          "O serviço é fornecido \"como está\" e \"conforme disponível\". Na máxima extensão permitida pela lei, renunciamos a todas as garantias, expressas ou implícitas, incluindo garantias de comercialização, adequação a um propósito específico e não-violação. Não prometemos que o serviço será ininterrupto, livre de erros ou adequado a todas as necessidades.",
        ],
      },
      {
        heading: "Limitação de responsabilidade",
        paragraphs: [
          "Na máxima extensão permitida pela lei, o Minuta e sua equipe não serão responsáveis por danos indiretos, incidentais, especiais, consequenciais ou punitivos, nem por qualquer perda de lucros, receitas, dados ou reputação decorrentes ou relacionados ao serviço.",
          "Nossa responsabilidade total e agregada por qualquer reclamação decorrente ou relacionada ao serviço não excederá o valor que você nos pagou pelo Minuta nos 12 meses imediatamente anteriores ao evento que deu origem à reclamação.",
          "Nada nestes Termos limita responsabilidades que não podem ser limitadas por lei (por exemplo, negligência grave, dolo ou direitos de consumidor que não podem ser renunciados).",
        ],
      },
      {
        heading: "Rescisão",
        paragraphs: [
          "Você pode parar de usar o Minuta e excluir sua conta a qualquer momento. Se tiver uma assinatura Pro, cancele pelo Portal do Cliente para interromper cobranças futuras.",
          "Podemos suspender ou encerrar seu acesso caso você descumpra estes Termos, mediante exigência legal, ou caso decidamos descontinuar o serviço. Daremos aviso razoável, salvo quando uma ação imediata for necessária para proteger usuários ou infraestrutura.",
        ],
      },
      {
        heading: "Mudanças nestes Termos",
        paragraphs: [
          "Podemos atualizar estes Termos periodicamente. Quando houver uma mudança material, atualizamos a data de \"última atualização\" acima e, se você tiver conta, avisamos por email. O uso continuado do serviço após a entrada em vigor da mudança significa que você aceita os Termos atualizados.",
        ],
      },
      {
        heading: "Lei aplicável",
        paragraphs: [
          "Estes Termos são regidos pelas leis de Portugal, sem atenção a suas regras de conflito de leis, e os tribunais de Portugal têm jurisdição exclusiva sobre qualquer disputa — exceto onde leis obrigatórias de proteção ao consumidor do seu país de residência disponham diferentemente.",
        ],
      },
    ],
    contact: "Dúvidas sobre estes Termos: support@minutapp.tech.",
  };
}

function es(): LegalDocument {
  return {
    lastUpdated: `Última actualización: ${LAST_UPDATED_ISO}`,
    preamble: [
      "Estos Términos de Servicio rigen el uso de la aplicación Minuta para macOS y del sitio Minuta en minutapp.tech. Al crear una cuenta, descargar la app o pagar una suscripción, aceptas estos Términos.",
      "Si no estás de acuerdo con alguna parte de estos Términos, no uses el servicio. Si tienes dudas antes de aceptar, escribe a support@minutapp.tech.",
    ],
    sections: [
      {
        heading: "El servicio",
        paragraphs: [
          "Minuta es una aplicación de escritorio para dispositivos macOS y Windows compatibles que captura el audio de las reuniones desde apps de tu computadora, transcribe localmente, traduce en tiempo real, identifica hablantes, resume la reunión y (en el plan Pro) sugiere respuestas mediante un copiloto de IA durante la propia reunión.",
          "Todo el procesamiento de audio, transcripción, traducción, resumen y razonamiento del copiloto se ejecuta en tu computadora. El servicio por nuestro lado se limita al sistema de cuentas, la facturación de la suscripción, el sitio público y el instalador disponible para descarga.",
        ],
      },
      {
        heading: "Elegibilidad",
        paragraphs: [
          "Debes tener al menos 16 años para usar Minuta. Al usar el servicio, confirmas tener capacidad para celebrar un contrato vinculante conforme a la ley que te aplique.",
        ],
      },
      {
        heading: "Tu cuenta",
        paragraphs: [
          "Necesitas una cuenta para iniciar sesión en la app de escritorio y gestionar una suscripción. Eres responsable de mantener tus credenciales seguras y de toda acción realizada desde tu cuenta. Avísanos a support@minutapp.tech si crees que tu cuenta ha sido comprometida.",
          "Una cuenta es para una persona. Puedes usarla en cuantos Macs tuyos quieras — no compartas credenciales con terceros.",
        ],
      },
      {
        heading: "Planes y facturación",
        paragraphs: [
          "Minuta ofrece un plan Gratis (1 hora de grabación por mes para transcripción y traducción locales después de cualquier período de prueba) y un plan Pro (grabación ilimitada, actas automáticas, identificación de hablantes, contexto personal y copiloto de IA en la reunión). Los precios aparecen en el checkout en tu moneda local; Stripe aplica el tipo de cambio del día cuando mostramos otra moneda.",
          "Pro se factura mensualmente y se renueva automáticamente hasta que canceles. El cancelamiento está disponible en cualquier momento mediante el Portal del Cliente de Stripe, enlazado en la página de tu cuenta. El acceso a Pro continúa hasta el final del período pagado en curso.",
          "Podemos cambiar precios o funciones del plan con aviso previo razonable; los cambios se aplican al siguiente ciclo de facturación.",
        ],
      },
      {
        heading: "Desistimiento y reembolsos",
        paragraphs: [
          "Si eres consumidor en la Unión Europea, tienes derecho legal de desistimiento durante los 14 días posteriores a la compra para servicios digitales, siempre que no hayas hecho un uso sustancial del servicio en ese período. Para ejercerlo, escribe a support@minutapp.tech antes de que se cumplan los 14 días.",
          "Fuera del plazo de desistimiento, las mensualidades no son reembolsables por el resto del período pagado. Aun así, consideramos reembolsos de buena fe en casos claros de fallos del servicio — escríbenos y lo revisamos.",
        ],
      },
      {
        heading: "Uso aceptable",
        paragraphs: [
          "Eres el único responsable de la legalidad de tu uso de Minuta. Las leyes de muchos países exigen el consentimiento de todos los participantes de una reunión antes de grabarla o transcribirla. Es tu responsabilidad conocer y cumplir la ley donde estén tú y los demás participantes.",
          "Aceptas no: usar Minuta para actividades ilegales; realizar ingeniería inversa, descompilar o manipular la app de escritorio más allá de lo permitido por las licencias open-source; intentar eludir la autenticación, los límites de uso o la facturación; sondear, escanear o explotar nuestra infraestructura; o usar el servicio de forma que perjudique a otros usuarios o a terceros.",
        ],
      },
      {
        heading: "Tu contenido",
        paragraphs: [
          "Las reuniones, transcripciones, traducciones, actas y cualquier documento que adjuntes como contexto del copiloto siguen siendo de tu propiedad. No accedemos a ellos, no reclamamos ninguna licencia sobre ellos y no los usamos para entrenar modelos de IA — viven en tu computadora y nuestros servidores nunca los ven.",
        ],
      },
      {
        heading: "Nuestra propiedad intelectual",
        paragraphs: [
          "El nombre Minuta, el logotipo, el contenido del sitio y las partes propietarias de la app de escritorio pertenecen al equipo Minuta. Mientras tu suscripción esté activa, te concedemos una licencia limitada, no transferible y no exclusiva para usar el software en reuniones personales o profesionales, conforme a estos Términos.",
          "La app de escritorio incluye componentes open-source (entre otros: whisper.cpp, llama.cpp y pesos de modelo bajo sus respectivas licencias). Esos componentes se rigen por sus propias licencias, reproducidas en el bundle de la app.",
        ],
      },
      {
        heading: "Resultados de IA",
        paragraphs: [
          "Las transcripciones, traducciones, resúmenes y sugerencias del copiloto se producen con modelos estadísticos y pueden contener errores, omisiones o alucinaciones. No los trates como registros autoritativos ni como asesoramiento legal, médico, financiero o profesional equivalente. Es tu responsabilidad revisar los resultados antes de actuar sobre ellos.",
        ],
      },
      {
        heading: "Exenciones",
        paragraphs: [
          "El servicio se ofrece \"tal cual\" y \"según disponibilidad\". En la máxima medida permitida por la ley, renunciamos a todas las garantías, expresas o implícitas, incluidas las de comerciabilidad, adecuación a un propósito concreto y no infracción. No prometemos que el servicio será ininterrumpido, libre de errores o adecuado para todas las necesidades.",
        ],
      },
      {
        heading: "Limitación de responsabilidad",
        paragraphs: [
          "En la máxima medida permitida por la ley, Minuta y su equipo no serán responsables por daños indirectos, incidentales, especiales, consecuenciales o punitivos, ni por pérdida alguna de beneficios, ingresos, datos o reputación derivados o relacionados con el servicio.",
          "Nuestra responsabilidad total agregada por cualquier reclamación derivada o relacionada con el servicio no excederá la cantidad que nos hayas pagado por Minuta en los 12 meses inmediatamente anteriores al hecho que originó la reclamación.",
          "Nada en estos Términos limita responsabilidades que no puedan limitarse por ley (por ejemplo, negligencia grave, conducta dolosa o derechos de consumidor irrenunciables).",
        ],
      },
      {
        heading: "Terminación",
        paragraphs: [
          "Puedes dejar de usar Minuta y eliminar tu cuenta en cualquier momento. Si tienes una suscripción Pro, cancélala desde el Portal del Cliente para detener facturaciones futuras.",
          "Podemos suspender o terminar tu acceso si incumples estos Términos, si la ley lo exige, o si decidimos discontinuar el servicio. Daremos aviso razonable salvo que se requiera una acción inmediata para proteger a usuarios o infraestructura.",
        ],
      },
      {
        heading: "Cambios en estos Términos",
        paragraphs: [
          "Podemos actualizar estos Términos periódicamente. Cuando realicemos un cambio material, actualizaremos la fecha de \"última actualización\" arriba y, si tienes cuenta, te avisaremos por correo. El uso continuado del servicio tras la entrada en vigor del cambio significa que aceptas los Términos actualizados.",
        ],
      },
      {
        heading: "Ley aplicable",
        paragraphs: [
          "Estos Términos se rigen por las leyes de Portugal, sin atender a sus normas de conflicto de leyes, y los tribunales de Portugal tendrán jurisdicción exclusiva sobre cualquier disputa — salvo cuando normas imperativas de protección al consumidor de tu país de residencia dispongan lo contrario.",
        ],
      },
    ],
    contact: "Consultas sobre estos Términos: support@minutapp.tech.",
  };
}
